import type { CSSProperties } from "react";
import {
  FONTE_DE_TEXTO_PADRAO_ID,
  estiloCssDaCamadaDeImagem,
  estiloCssDaFonte,
  fonteDeTexto,
  linhasDoTexto,
  type AlinhamentoDoTexto,
  type CamadaDeImagem,
  type CamadaDeTexto,
  type Tamanho,
} from "@foto/shared";

/**
 * O que a prévia desenha das camadas: a mesma conta do servidor, escrita para o navegador.
 *
 * **Imagem:** posição e tamanho em % da célula, girada em torno do centro (`estiloCssDaCamadaDeImagem`). A célula
 * do editor tem uns 300 px, a da prévia da folha uns 100 e o servidor monta em 300 dpi: só a fração vale para os três.
 *
 * **Texto: SVG, e não CSS.** O servidor desenha o contorno com junção **redonda** (um traço de `2 × espessura × corpo`
 * sob o preenchimento). O `-webkit-text-stroke` do CSS usa junção em ponta: com contorno grosso, cria espinhos nos
 * cantos das letras (o "W" de "AVW" ganha um chifre), e o `text-shadow` em círculo, que seria redondo, sai
 * serrilhado em quase todo navegador. O `<text>` do SVG tem `stroke-linejoin="round"` e `paint-order`, e é exatamente
 * o que o servidor faz. A medição é o que o servidor também mede (a largura de cada linha, com os avanços reais da
 * fonte, e o ascendente e o descendente dela), então o bloco, a âncora e as linhas caem no mesmo lugar.
 */

/** A camada de imagem: caixa em % da célula, girada em torno do centro (`estiloCssDaCamadaDeImagem`). */
export function estiloCssDaImagem(
  camada: Pick<CamadaDeImagem, "x" | "y" | "rotacao" | "largura">,
  imagem: Tamanho,
  celula: Tamanho,
): CSSProperties {
  const e = estiloCssDaCamadaDeImagem(camada, imagem, celula);
  return {
    position: "absolute",
    left: `${e.left}%`,
    top: `${e.top}%`,
    width: `${e.width}%`,
    height: `${e.height}%`,
    transform: e.transform,
  };
}

/**
 * O texto que a tela mostra: as linhas exatamente como o servidor as desenha (normalizadas, sem as pontas
 * em branco, a mesma lista que `desenharTextoDaCamada` mede).
 */
export const linhasDaPrevia = (camada: Pick<CamadaDeTexto, "texto">): string[] =>
  linhasDoTexto(camada.texto);

/** O texto inteiro, em linhas separadas por `\n`. */
export const textoDaPrevia = (camada: Pick<CamadaDeTexto, "texto">): string =>
  linhasDaPrevia(camada).join("\n");

/** O tamanho da letra em pixels: `corpo` é % da largura da célula (`corpoEmPx`, de `@foto/shared`). */
export const corpoDoTextoEmPx = (
  camada: Pick<CamadaDeTexto, "corpo">,
  larguraDaCelula: number,
): number => (camada.corpo / 100) * larguraDaCelula;

/**
 * A fonte da camada para o CSS: pelo mesmo caminho do servidor (`escolherArquivoDaFonte`), o peso e o itálico
 * efetivos, os eixos fixos da família e **sem síntese** — o navegador não inventa negrito nem itálico.
 */
export function estiloDaFonteDoTexto(
  camada: Pick<CamadaDeTexto, "fonteId" | "peso" | "italico">,
): CSSProperties {
  const fonte = fonteDeTexto(camada.fonteId) ?? fonteDeTexto(FONTE_DE_TEXTO_PADRAO_ID)!;
  return estiloCssDaFonte(fonte, camada.peso, camada.italico);
}

/**
 * O traço do contorno, como o servidor o desenha: `2 × espessura × corpo` de largura, centrado na borda da letra
 * e **por baixo** do preenchimento — sobra, por fora, `espessura × corpo`. `null` sem contorno.
 */
export function tracoDoTexto(
  camada: Pick<CamadaDeTexto, "contorno" | "corpo">,
  corpoPx: number,
): { cor: string; largura: number } | null {
  const contorno = camada.contorno;
  if (!contorno || !(contorno.espessura > 0)) return null;
  return { cor: contorno.cor, largura: 2 * contorno.espessura * corpoPx };
}

/** O que a tela mede da fonte e do texto, em pixels: a entrada de `layoutDoTexto`. */
export interface MedidasDoTexto {
  /** A largura de cada linha, com os avanços reais da fonte (`getComputedTextLength`). */
  larguras: readonly number[];
  /** Ascendente e descendente da fonte no corpo da camada (o que o `getBBox` do primeiro `<text>` dá). */
  ascendente: number;
  descendente: number;
}

export interface LinhaDoTexto {
  texto: string;
  /** Onde a linha começa no bloco (as alinhadas à direita e ao centro andam). */
  x: number;
  /** A linha de base da linha, a partir do topo do bloco. */
  y: number;
}

export interface LayoutDoTexto {
  /** O bloco: a maior linha por `linhas × corpo × entrelinha`. É o que a caixa de seleção e o clique usam. */
  largura: number;
  altura: number;
  linhas: LinhaDoTexto[];
}

/**
 * Onde cada linha cai no bloco: a conta de `desenharTextoDaCamada` (servidor), linha por linha. Cada linha tem
 * `corpo × entrelinha` de altura, e a fonte (ascendente + descendente) fica no **meio** dela; o bloco tem a largura da
 * linha mais larga e se alinha por linha.
 */
export function layoutDoTexto(args: {
  linhas: readonly string[];
  medidas: MedidasDoTexto;
  corpoPx: number;
  entrelinha: number;
  alinhamento: AlinhamentoDoTexto;
}): LayoutDoTexto {
  const { linhas, medidas, corpoPx, entrelinha, alinhamento } = args;
  const alturaDaLinha = corpoPx * entrelinha;
  const largura = Math.max(0, ...medidas.larguras);

  return {
    largura,
    altura: linhas.length * alturaDaLinha,
    linhas: linhas.map((texto, i) => {
      const w = medidas.larguras[i] ?? 0;
      return {
        texto,
        x:
          alinhamento === "esquerda"
            ? 0
            : alinhamento === "direita"
              ? largura - w
              : (largura - w) / 2,
        y:
          i * alturaDaLinha +
          (alturaDaLinha - (medidas.ascendente + medidas.descendente)) / 2 +
          medidas.ascendente,
      };
    }),
  };
}
